const calculateMedian = (arr: number[]): number => {
    arr.sort((a, b) => a - b);
    const middleIndex = Math.floor(arr.length / 2);

    if (arr.length % 2 === 0) {
        return (arr[middleIndex - 1] + arr[middleIndex]) / 2;
    } else {
        return arr[middleIndex];
    }
}

interface DataWithLocationId {
    locationId: string,
}

interface SummaryStats {
    location: string,
    total: number,
    median: number,
    average: number,
}

interface TherapistPerRxSummaryStats {
  location: string;
//   sampleSize: number; // number of Rxs in the location
  median: number;     // median therapists per Rx
  average: number;    // average therapists per Rx
  min: number;        // minimum therapists in an Rx
  max: number;        // maximum therapists in an Rx
}

interface RxLifespanSummaryStats {
  location: string;
  delta: string;
  median: number;
  average: number;
  min: number;
  max: number;
}

// input data is the data returned after calculating a specific patient/rx/schedule stats e.g. new patient counts
export const calculateSummaryStats = <T extends DataWithLocationId>(inputData: T[], key: keyof T ): SummaryStats[] => {
    const summaryStats: SummaryStats[] = [];

    const uniqueLocations = new Set(inputData.map(entry => entry.locationId));

    uniqueLocations.forEach(locationId => {
        // filter for current location
        const currentLoc = inputData.filter(entry => entry.locationId === locationId);

        // extract values to calculate stats from
        const values = currentLoc.map(item => item[key] as number);

        // calculate total
        const total = values.reduce((sum, val) => sum + val, 0);

        // calculate median
        const median = calculateMedian(values);

        // calculate average, depends on bin
        const average = total / currentLoc.length;

        summaryStats.push({
            location: locationId,
            total: total,
            median: median,
            average: average,
        });
    });

    return summaryStats;
}

// calculate summary stats for therapists per Rx (also usable for rx blanko stats)
export const calculateTherapistPerRxSummaryStats = <T extends DataWithLocationId>(inputData: T[], key: keyof T ): TherapistPerRxSummaryStats[] => {
    const summaryStats: TherapistPerRxSummaryStats[] = [];

    const uniqueLocations = new Set(inputData.map(entry => entry.locationId)); 
    
    uniqueLocations.forEach(locationId => {
        // filter for current location
        const currentLoc = inputData.filter(entry => entry.locationId === locationId);
        const sampleSize = currentLoc.length; // number of Rxs in the location

        // extract values to calculate stats from
        const values = currentLoc.map(item => item[key] as number);
        if (values.length === 0) {
            return; // skip if no values to avoid errors
        }        
        // calculate median
        const median = calculateMedian(values);

        // calculate average
        const total = values.reduce((sum, val) => sum + val, 0);
        const average = total / sampleSize;

        // calculate min and max
        const min = Math.min(...values);
        const max = Math.max(...values); 

        summaryStats.push({     
            location: locationId,
            // sampleSize: sampleSize,
            median: median,
            average: average,
            min: min,
            max: max,
        });
    });

    return summaryStats;
}

// calculate summary stats for rx lifespan deltas
export const calculateRxLifespanSummaryStats = <T extends DataWithLocationId & {lifespanDeltas: {rxDateToBooking: number, firstTreatmentToBooking: number, lastTreatmentToBooking: number}}>(inputData: T[]): RxLifespanSummaryStats[] => {
    const summaryStats: RxLifespanSummaryStats[] = [];

    const uniqueLocations = new Set(inputData.map(entry => entry.locationId));

    uniqueLocations.forEach(locationId => {
        // filter for current location
        const currentLoc = inputData.filter(entry => entry.locationId === locationId);
        
        if (currentLoc.length === 0) {
            return; // skip if no values to avoid errors
        }

        // Define delta types with their German labels
        const deltaTypes = [
            { key: 'rxDateToBooking', label: 'Rx → Abr.' },
            { key: 'firstTreatmentToBooking', label: 'Erst → Abr.' },
            { key: 'lastTreatmentToBooking', label: 'Letzte → Abr.' }
        ];

        deltaTypes.forEach(deltaType => {
            // extract values for this delta type
            const values = currentLoc.map(item => item.lifespanDeltas[deltaType.key as keyof typeof item.lifespanDeltas]);

            // calculate stats
            const median = calculateMedian([...values]);
            const average = values.reduce((sum, val) => sum + val, 0) / values.length;
            const min = Math.min(...values);
            const max = Math.max(...values);

            summaryStats.push({
                location: locationId,
                delta: deltaType.label,
                median,
                average,
                min,
                max,
            });
        });
    });

    return summaryStats;
}  